// src/components/Profile.jsx
import React, { useState, useEffect } from 'react';
import { Lock, Save, Upload, X } from 'lucide-react';
import { changePassword } from '../../services/apiService';
import { updateMyImage, getEmployeeImage } from '../../services/HRstaff';
import { getMyProfile } from '../../services/EmployeeService';

const Profile = () => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [profile, setProfile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const employeeId = localStorage.getItem('employeeId');

  // Inline styles
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      padding: '2rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    },
    contentContainer: {
      width: '100%',
      maxWidth: '900px',
      background: 'rgba(255, 255, 255, 0.98)',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)'
    },
    header: {
      background: 'linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%)',
      padding: '3rem 2.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '2rem',
      position: 'relative',
      overflow: 'hidden'
    },
    headerGlow: {
      position: 'absolute',
      top: '-50%',
      right: '-20%',
      width: '400px',
      height: '400px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      filter: 'blur(60px)',
      pointerEvents: 'none'
    },
    imageHolder: {
      position: 'relative',
      flexShrink: 0,
      zIndex: 1
    },
    avatar: {
      width: '120px',
      height: '120px',
      borderRadius: '50%',
      overflow: 'hidden',
      background: 'var(--card-bg, white)',
      border: '4px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      transition: 'transform 0.3s ease'
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    avatarFallback: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#667eea',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
      textTransform: 'uppercase'
    },
    removeBtn: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: '#ef4444',
      border: '2px solid white',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
    },
    info: {
      color: 'white',
      zIndex: 1
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      margin: '0 0 0.5rem 0',
      color: 'white',
      letterSpacing: '-0.5px'
    },
    subtitle: {
      margin: 0,
      fontSize: '1rem',
      color: 'rgba(255, 255, 255, 0.9)',
      fontWeight: 400
    },
    content: {
      padding: '2.5rem'
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#1f2937',
      margin: '0 0 2rem 0',
      letterSpacing: '-0.3px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '3rem'
    },
    formColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#374151',
      letterSpacing: '0.3px',
      textTransform: 'uppercase'
    },
    input: {
      padding: '0.875rem 1rem',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '1rem',
      color: '#1f2937',
      background: 'var(--card-bg, white)',
      outline: 'none',
      transition: 'all 0.2s ease'
    },
    inputError: {
      borderColor: '#ef4444'
    },
    uploadContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    uploadBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      background: 'linear-gradient(135deg,rgba(30, 113, 190, 0.95))',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
      width: 'fit-content'
    },
    fileName: {
      fontSize: '0.875rem',
      color: '#6b7280',
      padding: '0.5rem 1rem',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '1px dashed #d1d5db'
    },
    errorMessage: {
      padding: '1rem 1.25rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: 500,
      background: '#fef2f2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    successMessage: {
      padding: '1rem 1.25rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: 500,
      background: '#f0fdf4',
      color: '#166534',
      border: '1px solid #bbf7d0'
    },
    errorText: {
      fontSize: '0.813rem',
      color: '#ef4444',
      fontWeight: 500,
      marginTop: '0.25rem'
    },
    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: '1rem',
      borderTop: '1px solid #f3f4f6'
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.875rem 2rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      background: 'linear-gradient(135deg,rgba(30, 113, 190, 0.95),rgba(30, 113, 190, 0.95))',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
      outline: 'none'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontSize: '1.125rem',
      color: '#6b7280'
    }
  };

  // Load profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const user = await getMyProfile();
        setProfile(user);
        setUserName(`${user.firstName} ${user.lastName}`);

        if (user.employeeId) {
          try {
            const imgUrl = await getEmployeeImage(user.employeeId);
            setProfileImageUrl(imgUrl);
          } catch (err) {
            console.warn('No profile image found');
          }
        }
      } catch (err) {
        setErrorMessage('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'confirmPassword') {
      setPasswordMatch(value === formData.newPassword);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      setFormErrors({ image: 'Image must be under 3MB' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setFormErrors({ image: 'Please select an image file' });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setFormErrors({});
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    document.getElementById('imageInput').value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      let messages = [];

      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          setPasswordMatch(false);
          setLoading(false);
          return;
        }
        await changePassword(formData.oldPassword, formData.newPassword);
        messages.push('Password changed');
      }

      if (selectedFile) {
        const result = await updateMyImage(selectedFile);
        setProfileImageUrl(result.imageUrl + "?t=" + Date.now());
        messages.push('Photo updated');
        setSelectedFile(null);
        setPreviewUrl(null);
      }

      setSuccessMessage(messages.join(' & ') + ' successfully!');
      setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMatch(true);

    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Update failed';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profile) {
    return <div style={styles.loading}>Loading profile...</div>;
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.contentContainer}>
        <div style={styles.header}>
          <div style={styles.headerGlow}></div>
          <div style={styles.imageHolder}>
            <div style={styles.avatar}>
              {previewUrl ? (
                <img src={previewUrl} alt="Preview" style={styles.avatarImage} />
              ) : profileImageUrl ? (
                <img src={profileImageUrl} alt="Profile" style={styles.avatarImage} onError={() => setProfileImageUrl(null)} />
              ) : (
                <div style={styles.avatarFallback}>
                  {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                </div>
              )}
            </div>
            {previewUrl && (
              <button onClick={removeImage} style={styles.removeBtn}>
                <X size={16} />
              </button>
            )}
          </div>
          <div style={styles.info}>
            <h1 style={styles.title}>{userName || 'Loading...'}</h1>
            <p style={styles.subtitle}>Update your photo and password</p>
          </div>
        </div>

        <div style={styles.content}>
          <section>
            <h2 style={styles.sectionTitle}>Account Settings</h2>
            
            <form style={styles.form} onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formColumn}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Profile Photo</label>
                    <div style={styles.uploadContainer}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        id="imageInput"
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="imageInput" style={styles.uploadBtn}>
                        <Upload size={18} />
                        Upload New Photo
                      </label>
                      {selectedFile && (
                        <span style={styles.fileName}>{selectedFile.name}</span>
                      )}
                    </div>
                    {formErrors.image && (
                      <span style={styles.errorText}>{formErrors.image}</span>
                    )}
                  </div>
                </div>

                <div style={styles.formColumn}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Current Password</label>
                    <input
                      type="password"
                      name="oldPassword"
                      value={formData.oldPassword}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter current password"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter new password"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      style={{
                        ...styles.input,
                        ...((!passwordMatch) ? styles.inputError : {})
                      }}
                      placeholder="Confirm new password"
                    />
                    {!passwordMatch && (
                      <span style={styles.errorText}>Passwords don't match</span>
                    )}
                  </div>
                </div>
              </div>

              {errorMessage && <div style={styles.errorMessage}>{errorMessage}</div>}
              {successMessage && <div style={styles.successMessage}>{successMessage}</div>}

              <div style={styles.formActions}>
                <button 
                  type="submit" 
                  style={{
                    ...styles.button,
                    ...(loading || (!formData.newPassword && !selectedFile) ? styles.buttonDisabled : {})
                  }}
                  disabled={loading || (!formData.newPassword && !selectedFile)}
                >
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;